import { useCallback, useMemo } from 'react'
import { collection, doc, orderBy, query, serverTimestamp } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { fbt } from 'fbtee'
import { db } from '../lib/firebase'
import { updateDoc } from '../lib/firestore'
import { runMutation } from '../lib/firestoreMutation'
import { useFirestoreCollection } from './useFirestore'
import { useAuth } from './useAuth'
import { useAdminPendingDocs } from './useAdminPendingDocs'
import type { SetReportStatus, SetReportVerdict } from '../types'

const EMPTY: never[] = []

type ReportDoc = { id: string; status: SetReportStatus; verdict?: SetReportVerdict }
export type ReportCollection = 'setReports' | 'eventReports'

// Every report in a routine-checked report collection (setReports,
// eventReports), newest first, plus admin triage. Admin-only: non-admins run no
// query at all.
export function useAdminReportQueue<T extends ReportDoc>(collectionName: ReportCollection) {
  const { isAdmin, user } = useAuth()

  const reportsQuery = useMemo(
    () =>
      isAdmin
        ? (query(collection(db, collectionName), orderBy('createdAt', 'desc')) as Query<T>)
        : null,
    [isAdmin, collectionName],
  )

  const { data, loading, error } = useFirestoreCollection<T>(reportsQuery)

  const resolveReport = useCallback(
    async (id: string, status: SetReportStatus) => {
      const uid = user?.uid
      if (!uid) return
      await runMutation(
        fbt('Could not update the report', 'Error toast when an admin fails to triage a set report'),
        () => updateDoc(doc(db, collectionName, id), { status, resolvedAt: serverTimestamp(), resolvedBy: uid }),
      )
    },
    [user?.uid, collectionName],
  )

  return { reports: isAdmin ? data : (EMPTY as T[]), loading, error, resolveReport }
}

/** A pending report the routine has had its say on — what's left for a person. */
export function needsAdmin(report: Pick<ReportDoc, 'status' | 'verdict'>): boolean {
  return report.status === 'pending' && !!report.verdict
}

// Reports still waiting on the routine's verdict don't count: it settles most
// of them within minutes.
export function useAdminPendingReportCount(collectionName: ReportCollection): number {
  return useAdminPendingDocs<ReportDoc>(collectionName).filter(needsAdmin).length
}
